/**
 * The decks a report can be re-run against, for the viewer's `Deck` control. Sourced from `/pnl-decks`
 * rather than the spec's `deck_overrides`, which a studio report usually leaves empty. The report's
 * own deck leads the list, so the control opens on what is showing.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { listPnlDecks } from 'src/components/star-grid/services/starGridService';

export interface UseReportDeckTypesResult {
  readonly options: readonly { value: string; label: string }[];
  /** Empty until the user picks one, so an untouched report runs against its own default deck. */
  readonly value: string;
  readonly setValue: (next: string) => void;
}

/** Deck names that are acronyms, which the deck writes in full caps rather than title case. */
const DECK_ACRONYMS = /^(mec|op\d+|hc|gp|ww|pl|d&s)$/i;

/** `mec` → `MEC`, `op2 (annual)` → `OP2 (Annual)` — the catalogue stores them lowercased. */
const deckLabel = (deck: string): string =>
  deck.replace(/[a-z0-9&]+/gi, (word) => (DECK_ACRONYMS.test(word) ? word.toUpperCase() : word.charAt(0).toUpperCase() + word.slice(1)));

export const useReportDeckTypes = (reportDeck?: string, enabled = true): UseReportDeckTypesResult => {
  const [decks, setDecks] = useState<readonly string[]>([]);
  const [value, setValue] = useState<string>('');
  const loaded = useRef(false);

  useEffect(() => {
    // Ref guard, not effect cleanup: React StrictMode re-runs the effect in development, and a
    // `live` cleanup that cancels the one in-flight fetch while the ref guard skips the second run
    // leaves the catalogue permanently empty (same hazard `useDefaultStartingPoint` avoids). The
    // one fetch always applies; a late resolve after unmount is a harmless no-op setState.
    if (!enabled || loaded.current) return;
    loaded.current = true;
    listPnlDecks()
      .then((groups) => {
        const found = groups.map((group) => (group.deck_type ?? '').trim()).filter((deck) => deck.length > 0);
        setDecks([...new Set(found)]);
      })
      // An unavailable catalogue leaves the control a fixed value rather than breaking the report.
      .catch(() => undefined);
  }, [enabled]);

  const options = useMemo(() => {
    const own = (reportDeck ?? '').trim();
    const rest = decks.filter((deck) => deck.toLowerCase() !== own.toLowerCase());
    return [...(own ? [own] : []), ...rest].map((deck) => ({ value: deck, label: deckLabel(deck) }));
  }, [decks, reportDeck]);

  return { options, value, setValue };
};
